import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, FileText, TrendingUp } from "lucide-react";

interface HealthScoreModalProps {
  open: boolean;
  onClose: () => void;
  score: number;
  breakdown: {
    score: number;
    components: {
      lab_score: number;
      lab_weight: number;
      completeness_score: number;
      completeness_weight: number;
      vital_score: number;
      vital_weight: number;
    };
    metrics: {
      total_lab_results: number;
      abnormal_results: number;
      abnormal_percentage: number;
      vital_signs_count: number;
      completeness_percentage: number;
    };
  };
}

export function HealthScoreModal({ open, onClose, score, breakdown }: HealthScoreModalProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-emerald-600";
    if (score >= 60) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return "Excelente";
    if (score >= 60) return "Bom";
    if (score >= 40) return "Regular";
    return "Necessita Atenção";
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Detalhamento do Score de Saúde
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Score Principal */}
          <Card className="border-2 border-primary/20 bg-primary/5">
            <CardContent className="pt-6">
              <div className="text-center">
                <div className={`text-5xl font-bold mb-2 ${getScoreColor(score)}`}>
                  {score}/100
                </div>
                <Badge variant="outline" className="text-sm">
                  {getScoreLabel(score)}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Metodologia */}
          <div>
            <h3 className="font-semibold mb-3 text-sm">Metodologia de Cálculo</h3>
            <p className="text-sm text-muted-foreground mb-4">
              O Score de Saúde é calculado com base em 3 componentes principais dos 
              registros de saúde do paciente, cada um com peso diferente na composição final:
            </p>
          </div>

          {/* Componentes */}
          <div className="space-y-4">
            {/* Resultados Laboratoriais */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3 mb-3">
                  <FileText className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm">Resultados Laboratoriais</h4>
                      <Badge variant="outline">Peso: {breakdown.components.lab_weight}%</Badge>
                    </div>
                    <Progress value={breakdown.components.lab_score} className="mb-2" />
                    <p className="text-xs text-muted-foreground mb-2">
                      Score: {breakdown.components.lab_score}/100
                    </p>
                  </div>
                </div>
                <div className="bg-muted/50 p-3 rounded text-xs space-y-1">
                  <p>• Total de exames com referências: {breakdown.metrics.total_lab_results}</p>
                  <p>• Resultados fora da faixa: {breakdown.metrics.abnormal_results} 
                     ({breakdown.metrics.abnormal_percentage}%)</p>
                  <p className="text-muted-foreground mt-2">
                    Quanto menor o percentual de resultados anormais, maior o score deste componente.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Completude dos Dados */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3 mb-3">
                  <TrendingUp className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm">Completude dos Dados</h4>
                      <Badge variant="outline">Peso: {breakdown.components.completeness_weight}%</Badge>
                    </div>
                    <Progress value={breakdown.components.completeness_score} className="mb-2" />
                    <p className="text-xs text-muted-foreground mb-2">
                      Score: {breakdown.components.completeness_score}/100
                    </p>
                  </div>
                </div>
                <div className="bg-muted/50 p-3 rounded text-xs space-y-1">
                  <p>• Completude geral: {breakdown.metrics.completeness_percentage}%</p>
                  <p className="text-muted-foreground mt-2">
                    Avalia a qualidade e completude dos registros (valores preenchidos, 
                    referências, unidades, datas).
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Sinais Vitais */}
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3 mb-3">
                  <Activity className="h-5 w-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm">Sinais Vitais</h4>
                      <Badge variant="outline">Peso: {breakdown.components.vital_weight}%</Badge>
                    </div>
                    <Progress value={breakdown.components.vital_score} className="mb-2" />
                    <p className="text-xs text-muted-foreground mb-2">
                      Score: {breakdown.components.vital_score}/100
                    </p>
                  </div>
                </div>
                <div className="bg-muted/50 p-3 rounded text-xs space-y-1">
                  <p>• Total de registros de sinais vitais: {breakdown.metrics.vital_signs_count}</p>
                  <p className="text-muted-foreground mt-2">
                    Avalia a presença e qualidade dos registros de sinais vitais (PA, FC, temperatura, etc).
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Fórmula */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4">
              <h4 className="font-semibold text-sm mb-3">Fórmula de Cálculo</h4>
              <div className="font-mono text-xs bg-background p-3 rounded">
                Score Final = (Lab Score × 0.5) + (Completude × 0.3) + (Sinais Vitais × 0.2)
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                O score é dinâmico e se atualiza automaticamente conforme novos registros 
                são adicionados ao prontuário do paciente.
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
